// Unit / building / research definitions for the single playable faction
// ("Vanguard"). Both players use this faction — the prototype is a mirror
// matchup. Stats are BW-flavoured: damage types vs size classes, real build
// times in game-seconds, supply costs, and a strict tech dependency tree.

// size classes drive the damage-type multiplier table in constants.js
export const SIZE = { SMALL: 0, MEDIUM: 1, LARGE: 2 };

export const UnitDefs = {
  worker: {
    id: 'worker', name: 'Worker', hotkey: 'W', short: 'WRK',
    cost: { minerals: 50, gas: 0 }, supply: 1, buildTime: 17,
    hp: 60, armor: 0, size: SIZE.SMALL, radius: 0.35, speed: 4.9, sight: 7,
    isWorker: true, canBuild: true,
    weapon: { damage: 5, type: 'normal', cooldown: 0.6, range: 0.6, targets: 'ground', splash: 0, projectile: null },
    desc: 'Gathers minerals and gas. Constructs all structures.',
  },
  rifleman: {
    id: 'rifleman', name: 'Rifleman', hotkey: 'R', short: 'RIF',
    cost: { minerals: 50, gas: 0 }, supply: 1, buildTime: 15,
    hp: 40, armor: 0, size: SIZE.SMALL, radius: 0.35, speed: 4.0, sight: 7,
    weapon: { damage: 6, type: 'normal', cooldown: 0.63, range: 4.5, targets: 'any', splash: 0, projectile: 'bullet' },
    desc: 'Basic ranged infantry. Hits air and ground.',
  },
  bruiser: {
    id: 'bruiser', name: 'Bruiser', hotkey: 'B', short: 'BRU',
    cost: { minerals: 75, gas: 0 }, supply: 2, buildTime: 20,
    hp: 110, armor: 1, size: SIZE.MEDIUM, radius: 0.40, speed: 4.9, sight: 7,
    weapon: { damage: 14, type: 'concussive', cooldown: 1.0, range: 1.0, targets: 'ground', splash: 0, projectile: null },
    desc: 'Melee shock infantry. Concussive damage shreds light units, weak vs armour.',
  },
  siege_tank: {
    id: 'siege_tank', name: 'Siege Tank', hotkey: 'S', short: 'TNK',
    cost: { minerals: 150, gas: 100 }, supply: 3, buildTime: 32,
    hp: 150, armor: 1, size: SIZE.LARGE, radius: 0.55, speed: 4.4, sight: 10,
    canSiege: true, siegeTransition: 2.0,
    weapon: { damage: 30, type: 'explosive', cooldown: 1.5, range: 7.0, targets: 'ground', splash: 0, projectile: 'shell' },
    siegeWeapon: { damage: 70, type: 'explosive', cooldown: 3.0, range: 12.0, minRange: 2.0, targets: 'ground', splash: 1.5, projectile: 'arc' },
    siegeSight: 12,
    desc: 'Siege mode trades mobility for 12-range splash artillery. Splash damages friendlies.',
  },
  wraith: {
    id: 'wraith', name: 'Wraith', hotkey: 'W', short: 'WRA',
    cost: { minerals: 150, gas: 100 }, supply: 2, buildTime: 40,
    hp: 120, armor: 0, size: SIZE.LARGE, radius: 0.40, speed: 6.6, sight: 7,
    flying: true, detector: true, canCloak: true, cloakCost: 0,
    weapon: { damage: 20, type: 'normal', cooldown: 2.2, range: 5.0, targets: 'any', splash: 0, projectile: 'laser' },
    desc: 'Air superiority fighter. Detector. Can cloak once Cloaking Field is researched.',
  },
};

export const BuildingDefs = {
  command_center: {
    id: 'command_center', name: 'Command Center', hotkey: 'C', short: 'CC',
    cost: { minerals: 400, gas: 0 }, buildTime: 75,
    hp: 1500, armor: 1, size: 4, sight: 10, supplyProvided: 10,
    produces: ['worker'], research: [], requires: [], isTownHall: true,
    desc: 'Produces workers and accepts resource dropoff. Provides 10 supply.',
  },
  depot: {
    id: 'depot', name: 'Supply Depot', hotkey: 'S', short: 'DEP',
    cost: { minerals: 100, gas: 0 }, buildTime: 25,
    hp: 500, armor: 1, size: 3, sight: 8, supplyProvided: 8,
    produces: [], research: [], requires: [],
    desc: 'Provides 8 supply.',
  },
  refinery: {
    id: 'refinery', name: 'Refinery', hotkey: 'R', short: 'REF',
    cost: { minerals: 100, gas: 0 }, buildTime: 25,
    hp: 750, armor: 1, size: 3, sight: 8, onGeyser: true,
    produces: [], research: [], requires: [],
    desc: 'Built on a vespene geyser. Up to 3 workers may harvest gas.',
  },
  barracks: {
    id: 'barracks', name: 'Barracks', hotkey: 'B', short: 'BRK',
    cost: { minerals: 150, gas: 0 }, buildTime: 50,
    hp: 1000, armor: 1, size: 4, sight: 8,
    produces: ['rifleman', 'bruiser'], research: [], requires: ['depot'],
    desc: 'Trains Riflemen and Bruisers.',
  },
  factory: {
    id: 'factory', name: 'Factory', hotkey: 'F', short: 'FAC',
    cost: { minerals: 200, gas: 100 }, buildTime: 50,
    hp: 1250, armor: 1, size: 4, sight: 8,
    produces: ['siege_tank'], research: ['siege_tech'], requires: ['barracks'],
    desc: 'Trains Siege Tanks. Researches Siege Tech.',
  },
  starport: {
    id: 'starport', name: 'Starport', hotkey: 'P', short: 'STA',
    cost: { minerals: 150, gas: 100 }, buildTime: 60,
    hp: 1300, armor: 1, size: 4, sight: 8,
    produces: ['wraith'], research: ['cloaking_field'], requires: ['factory'],
    desc: 'Trains Wraiths. Researches Cloaking Field.',
  },
  turret: {
    id: 'turret', name: 'Missile Turret', hotkey: 'T', short: 'TUR',
    cost: { minerals: 75, gas: 0 }, buildTime: 30,
    hp: 200, armor: 0, size: 2, sight: 11, detector: true,
    produces: [], research: [], requires: ['barracks'],
    weapon: { damage: 20, type: 'explosive', cooldown: 1.0, range: 7.0, targets: 'air', splash: 0, projectile: 'missile' },
    desc: 'Static anti-air defence. Detects cloaked units.',
  },
};

export const ResearchDefs = {
  siege_tech: {
    // 'S' would collide with Siege Tank on the Factory's command card, making
    // the research unreachable by its own key.
    id: 'siege_tech', name: 'Siege Tech', hotkey: 'T',
    cost: { minerals: 150, gas: 150 }, researchTime: 60, building: 'factory',
    desc: 'Enables Siege Mode for Siege Tanks.',
  },
  cloaking_field: {
    id: 'cloaking_field', name: 'Cloaking Field', hotkey: 'C',
    cost: { minerals: 150, gas: 150 }, researchTime: 60, building: 'starport',
    desc: 'Enables cloaking for Wraiths.',
  },
};

export const ResourceDefs = {
  mineral: { id: 'mineral', name: 'Mineral Field', w: 2, h: 1, radius: 0.9 },
  geyser:  { id: 'geyser',  name: 'Vespene Geyser', w: 3, h: 3, radius: 1.5 },
};

export function unitDef(id) { return UnitDefs[id]; }
export function buildingDef(id) { return BuildingDefs[id]; }
export function defOf(e) {
  if (e.kind === 'unit') return UnitDefs[e.defId];
  if (e.kind === 'building') return BuildingDefs[e.defId];
  return ResourceDefs[e.defId];
}

// Which structure trains a given unit — used by the AI and by the UI to route
// train orders, and to validate tech before spending.
export const TRAINER_OF = (() => {
  const m = {};
  for (const b of Object.values(BuildingDefs)) for (const u of b.produces) m[u] = b.id;
  return m;
})();

// Full transitive tech requirements for a unit or building id.
export function techRequirements(id) {
  const out = new Set();
  const visit = (bid) => {
    const b = BuildingDefs[bid];
    if (!b) return;
    for (const r of b.requires) { out.add(r); visit(r); }
  };
  if (BuildingDefs[id]) { visit(id); }
  else if (UnitDefs[id]) { const t = TRAINER_OF[id]; if (t) { out.add(t); visit(t); } }
  return [...out];
}
